import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'

import { PathConfiguration, MenuItem, ResourceType, Resource } from '@mprep/shared/data/models'
import { PathService, ResourceService } from '@mprep/shared/data/services'

import { MenuItemFactory } from './'
import { MenuItemService } from '../menu-item.service'

type ExperienceMenuFuction = (rootMenuItem: MenuItem, pathConfig: PathConfiguration) => Observable<MenuItem>

@Injectable()
export class MenuService {

  constructor(
    private _menuItemService: MenuItemService,
    private _menuItemFactory: MenuItemFactory,
    private _pathService: PathService,
    private _resourceService: ResourceService
  ) {}

  public getMenu(pathConfig: PathConfiguration): Observable<MenuItem> {
    const experienceFn: ExperienceMenuFuction = this._determineFactoryMethodForExperience(pathConfig).bind(this)
    return this._menuItemService.findModelById(pathConfig.rootMenuItem.id, ['menuItems.menuItems.menuItems'])
      .concatMap((menuItem: MenuItem) => {
        const menuClone = menuItem.clone()
        this._normalizeAction(menuClone.menuItems)
        this._wrapMenuItemsWithNoChildrenInBlankContainers(menuClone)
        return experienceFn(menuClone, pathConfig)
      })
  }

  private _wrapMenuItemsWithNoChildrenInBlankContainers(rootMenuItem: MenuItem) {
    rootMenuItem.menuItems.forEach((menuItem, index) => {
      if (!menuItem.hasMenuItems()) {
        rootMenuItem.menuItems[index] = this._appendToContainer(menuItem)
      }
    })
  }

  private _determineFactoryMethodForExperience(pathConfig: PathConfiguration): ExperienceMenuFuction {
    switch (pathConfig.experienceType) {
      case 1: return this._createPathExperience
      case 2: return this._createTileExperience
      default: return this._createDefaultExperience
    }
  }

  private _createPathExperience(rootMenuItem: MenuItem, pathConfig: PathConfiguration): Observable<MenuItem> {
    const menu = rootMenuItem || new MenuItem()
    const pathId = pathConfig.rootPathNode.id
    this._prependItemWith(menu,
      this._menuItemFactory.createHome(),
      this._menuItemFactory.createStarredTasks(pathId)
    )
    return this._createSyllabusSectionForPathExperience(menu, pathConfig)
  }

  private _createDefaultExperience(rootMenuItem: MenuItem, pathConfig: PathConfiguration): Observable<MenuItem> {
    return this._createTileExperience(rootMenuItem, pathConfig)
  }

  private _createTileExperience(rootMenuItem: MenuItem, pathConfig: PathConfiguration): Observable<MenuItem> {
    const menu = rootMenuItem || new MenuItem()
    this._prependItemWith(menu,
      this._menuItemFactory.createHome(),
    )
    return this._createSyllabusSectionForTileExperience(menu, pathConfig)
  }

  private _createSyllabusSectionForPathExperience(menu: MenuItem, pathConfig: PathConfiguration): Observable<MenuItem> {
    return this._resolveSyllabusSwitch().map((syllabusSwitchMenu) => {
      const menusToAdd = [
        syllabusSwitchMenu,
        this._menuItemFactory.createSyllabusReset(pathConfig.rootPathNode),
        this._createTour(),
      ].filter((_menu) => _menu)
      return this._appendSectionToRootMenu('Syllabus', menu, menusToAdd)
    })
  }

  private _createSyllabusSectionForTileExperience(menu: MenuItem, _pathConfig: PathConfiguration): Observable<MenuItem> {
    return this._resolveSyllabusSwitch().map((syllabusSwitchMenu) => {
      const menusToAdd = [
        syllabusSwitchMenu,
        this._createTour()
      ].filter((_menu) => _menu)
      return this._appendSectionToRootMenu('Syllabus', menu, menusToAdd)
    })
  }

  private _prependItemWith(parent: MenuItem, ...menuItem: MenuItem[]) {
    parent.menuItems.unshift(this._appendToContainer(...menuItem))
  }

  private _appendToContainer(...menuItem: MenuItem[]) {
    const section = this._menuItemFactory.createBlankSection()
    section.menuItems = menuItem
    return section
  }

  private _appendSectionToRootMenu(title: string, root: MenuItem, menuItems: MenuItem[]) {
    if (menuItems.length > 0) {
      const section = this._menuItemFactory.createSection(title)
      section.menuItems = menuItems
      root.menuItems.push(section)
    }
    return root
  }

  private _resolveSyllabusSwitch(): Observable<MenuItem> {
    return this._pathService.getPaths([]).map((paths) => {
      if (paths.length > 1) { return this._menuItemFactory.createSyllabusSwitch() }
    })
  }

  private _createTour(): MenuItem {
    const tourFunctionName = `tourExperience`
    return this._menuItemFactory.createTour(tourFunctionName)
  }

  private _normalizeAction(menuItems: MenuItem[]) {
    menuItems.forEach((menuItem) => {
      if (!menuItem.action) {
        this._determineMenuItemAction(menuItem)
      }
      this._normalizeAction(menuItem.menuItems)
    })
  }

  private _determineMenuItemAction(menuItem: MenuItem) {
    menuItem.action = {
      type: 'none' as 'none',
      value: ''
    }
    if (menuItem.hasResource()) {
      this._resourceService.findModelById(menuItem.resourceId.toString())
        .subscribe((resource) => {
          menuItem.action = this._getResourceAction(resource)
        })
    }
  }

  private _getResourceAction(resource: Resource) {
    const actions = {}
    actions[ResourceType.Atlas] = { type: 'routerlink', value: resource.uri }
    const defaultAction = { type: 'resource', value: resource.id }

    return actions[resource.resourceType] || defaultAction
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/menu/menu.service.ts